import { useEffect, useRef } from "react";
import { Chart, ChartConfiguration, registerables } from "chart.js";
import { Card, CardContent, Typography } from "@mui/material";
import { TableChartData } from "./stats.page";

Chart.register(...registerables);

interface ChartCardProps {
  table: TableChartData;
}

export default function TableChartCard({ table }: ChartCardProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);

  const totalSum = table.results.reduce((acc, r) => acc + r.result, 0);

  const getChartColor = (tableId: number) => {
    const colors = [
      "#4FD1C5",
      "#F6AD55",
      "#F56565",
      "#68D391",
      "#63B3ED",
      "#D53F8C",
    ];
    return colors[tableId % colors.length];
  };

  useEffect(() => {
    if (!canvasRef.current) return;

    if (chartRef.current) {
      chartRef.current.destroy();
    }

    const ctx = canvasRef.current.getContext("2d");
    if (!ctx) return;

    const gradient = ctx.createLinearGradient(0, 0, 0, 300);
    gradient.addColorStop(0, "rgba(79, 209, 197, 0.5)");
    gradient.addColorStop(1, "rgba(79, 209, 197, 0)");

    const config: ChartConfiguration = {
      type: "line",
      data: {
        labels: table.results.map((r) => r.date),
        datasets: [
          {
            label: table.tableName,
            data: table.results.map((r) => r.result),
            borderColor: getChartColor(table.tableId),
            backgroundColor: gradient,
            borderWidth: 2,
            pointRadius: 4,
            tension: 0.4,
            fill: true,
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: (context) => `Amount: ${context.raw} USD`,
            },
          },
        },
        scales: {
          x: {
            ticks: {
              color: "rgba(255, 255, 255, 0.9)",
            },
            grid: {
              color: "rgba(255, 255, 255, 0.5)",
            },
          },
          y: {
            ticks: {
              color: "rgba(255, 255, 255, 0.9)",
            },
            grid: {
              color: "rgba(255, 255, 255, 0.05)",
            },
          },
        },
      },
    };

    chartRef.current = new Chart(ctx, config);
  }, [table]);

  return (
    <Card
      sx={{
        p: 3,
        mb: 4,
        background:
          "linear-gradient(135deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0.015))",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        backdropFilter: "blur(16px)",
        borderRadius: "15px",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.25)",
        transition: "transform 0.3s",
        "&:hover": {
          transform: "scale(1.02)",
        },
      }}
    >
      <CardContent>
        <Typography
          variant="h6"
          sx={{
            mb: 1,
            textTransform: "uppercase",
            letterSpacing: 1,
            fontWeight: 500,
          }}
        >
          {table.tableName}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Total: {totalSum} USD
        </Typography>

        <canvas ref={canvasRef} />
      </CardContent>
    </Card>
  );
}
